module Client.Api
open System
open Elmish
open Fable.Remoting.Client
open Shared
open Fable.SimpleJson

module HttpResult =
    type ErrorResult =
        {
            error: string
            ignored: bool
            handled: bool
        }
    let inline processResult (call: ('arg -> 'ret Async)) (arg: 'arg) =
        async {
            let! result = Async.Catch (call arg) 
            match result with 
            | Choice1Of2 output -> return Result.Ok output
            | Choice2Of2 ex ->
                match ex with  
                | :? ProxyRequestException as ex -> 
                    //let response : HttpResponse = ex.Response
                    return
                        {
                            StatusCode = ex.StatusCode
                            ErrorMessage =
                                Json.tryParseAs<ErrorResult> ex.ResponseText
                                |> function
                                    | Result.Ok ret -> Some ret.error
                                    | Result.Error _ -> None
                        }
                        |> ApiError.ProxyError
                        |> Result.Error
                | exn -> return ApiError.AsyncError exn |> Result.Error
        }

module Request = 
    let createApi subRoute = Remoting.createApi() |> Remoting.withRouteBuilder (Route.clientSideBuilder subRoute)

    let createSecureApi subRoute =
        Storage.tryGet<ClientSideUser> Storage.USER_STORAGE_KEY
        |> Option.map (fun user -> createApi (SubRoute.Secured subRoute) |> Remoting.withAuthorizationHeader (sprintf "Bearer %s" user.Token))

    let createMFAApi() =
        Storage.tryGet<ClientSideUser> Storage.USER_STORAGE_KEY
        |> Option.map (fun user -> createApi SubRoute.MFA |> Remoting.withAuthorizationHeader (sprintf "Bearer %s" user.Token))

    let inline sendAndCatchFailure method (arg: 'arg) (wrapSuccess: 'ret -> 'msg) (wrapFailure: ApiError -> 'msg) =
        Cmd.OfAsync.perform
            (HttpResult.processResult method)
            arg
            (function
             | Result.Ok ret -> wrapSuccess ret
             | Result.Error err -> wrapFailure err)

let inline doNothing() =
    async {
        ()
    }

let inline secureCall<'contract, 'arg, 'ret, 'msg> subRoute (call: 'contract -> ('arg -> 'ret Async)) (arg: 'arg) (wrapSuccess: 'ret -> 'msg) (wrapFailure: ApiError -> 'msg) =
    match Request.createSecureApi subRoute with
    | None -> Cmd.OfAsync.perform (fun _ -> doNothing()) () (fun _ -> wrapFailure ApiError.NoUser)
    | Some opts ->
        let api = Remoting.buildProxy<'contract> opts
        Request.sendAndCatchFailure (call api) arg wrapSuccess wrapFailure

let inline mfaCall<'contract, 'arg, 'ret, 'msg> (call: 'contract -> ('arg -> 'ret Async)) (arg: 'arg) (wrapSuccess: 'ret -> 'msg) (wrapFailure: ApiError -> 'msg) =
    match Request.createMFAApi() with
    | None -> Cmd.OfAsync.perform (fun _ -> doNothing()) () (fun _ -> wrapFailure ApiError.NoUser)
    | Some opts ->
        let api = Remoting.buildProxy<'contract> opts
        Request.sendAndCatchFailure (call api) arg wrapSuccess wrapFailure

let inline unsecureCall<'contract, 'arg, 'ret, 'msg> (call: 'contract -> ('arg -> 'ret Async)) (arg: 'arg) (wrapSuccess: 'ret -> 'msg) (wrapFailure: ApiError -> 'msg) =
    let api = Request.createApi SubRoute.Unsecured |> Remoting.buildProxy<'contract>
    Request.sendAndCatchFailure (call api) arg wrapSuccess wrapFailure

let inline unsecureCallSuccess<'contract, 'arg, 'ret, 'msg> (call: 'contract -> ('arg -> 'ret Async)) (arg: 'arg) (wrapSuccess: 'ret -> 'msg) =
    let api = Request.createApi SubRoute.Unsecured |> Remoting.buildProxy<'contract>
    Cmd.OfAsync.perform (call api) arg wrapSuccess

let inline downloadFile<'contract, 'arg, 'msg> subRoute (call: 'contract -> ('arg -> (string * byte[]) Async)) (arg: 'arg) (wrapSuccess: unit -> 'msg) (wrapFailure: ApiError -> 'msg) =
    let download arg = 
        async {
            match Request.createSecureApi subRoute with
            | Some opts ->
                let api = Remoting.buildProxy<'contract> opts
                let! (name, data) = call api arg
                data.SaveFileAs(name)
            | None -> ()
        }

    Request.sendAndCatchFailure download arg wrapSuccess wrapFailure